/**
 * TEC navbar redesign Phase 4 item 1: the single source of truth for every row-one/row-two
 * control's class strings, shared by the hydrated header (NavigationNavbar's live-index branch
 * and its nav-live-index/* components) and the prerendered static twin (ResourceNavbar.tsx +
 * the vanilla-JS island it injects, web-static/pages/landing/landing-user-menu-script.ts).
 *
 * Before this file, the two renderers kept independent copies of the same class strings and
 * drifted (see the Phase 4 spec's side-by-side DOM diff). Every export here is a plain string
 * (or a small pure function returning one) with no React/hooks dependency, so it is safe to
 * import from both a React component and a plain TS string-template script.
 *
 * `data-jc-nav-part="<name>"` is the drift-guard hook: both renderers stamp the same part name
 * on the equivalent element, and components/__tests__/primary-nav-uniformity.test.tsx renders
 * both to static markup and asserts the `class` attribute is byte-identical for every part, in
 * all three locales. Changing a class string here (or in only one renderer) must fail that test
 * unless both sides are updated together.
 */

export const NAV_PART = {
  logoHorizontal: 'logo-horizontal',
  logoCompact: 'logo-compact',
  mobileSubtitle: 'mobile-subtitle',
  searchWrapper: 'search-wrapper',
  searchInput: 'search-input',
  locationSegment: 'location-segment',
  langTrack: 'lang-track',
  langCircle: 'lang-circle',
  accountPill: 'account-pill',
  accountAvatar: 'account-avatar',
  menuButton: 'menu-button',
  stripFigure: 'strip-figure',
  stripFigureValue: 'strip-figure-value',
  stripWord: 'strip-word',
  stripDivider: 'strip-divider',
  stripStamp: 'strip-stamp',
  // TEC navbar redesign item 2: the mobile bottom bar's own parts. Kept distinct from
  // searchWrapper/searchInput above (row one's search bar) rather than reused: the two bars use
  // different sizeVariants (compact44 vs compact40), so sharing a part name would make the
  // drift-guard's "every instance of a part shares one class string" rule fail for a legitimate
  // reason (different heights), not a real drift.
  mobileBottomBar: 'mobile-bottom-bar',
  mobileBottomBarFilters: 'mobile-bottom-bar-filters',
  mobileBottomBarMenu: 'mobile-bottom-bar-menu',
} as const;

// ── Logo (matches components/navigation/Logo.tsx variant="horizontal"/"compact" and
// NavigationNavbar's own inline mobile block) ──
export const LOGO_HORIZONTAL_WRAP =
  'hidden xl:inline-flex xl:items-center gap-2 cursor-pointer h-10 w-auto rounded-xl transition-colors hover:bg-jc-elevated';
export const LOGO_HORIZONTAL_IMG_PX = 40;
export const LOGO_HORIZONTAL_TEXT_CLASS = 'text-xl';

export const LOGO_COMPACT_WRAP = 'xl:hidden flex flex-col justify-center gap-0.5 min-w-0 cursor-pointer';
export const LOGO_COMPACT_ROW = 'flex items-center gap-2';
export const LOGO_COMPACT_IMG_PX = 36;
export const LOGO_COMPACT_IMG_CLASS = 'object-contain h-9 w-9 flex-shrink-0';
export const LOGO_COMPACT_TEXT_CLASS = 'text-lg';

export const MOBILE_SUBTITLE_WRAP = 'lg:hidden pl-[44px]';
export const MOBILE_SUBTITLE_LINE =
  'flex items-center gap-1.5 h-[15px] font-display font-medium text-[11.5px] text-jc-ink-secondary tabular-nums';
export const MOBILE_SUBTITLE_DOT = 'w-1.5 h-1.5 rounded-full bg-jc-accent flex-shrink-0';

// ── Search + location (matches nav-live-index/SearchLauncher.tsx's FallbackLauncher/LiveLauncher
// wrappers and LocationSegment; the SRP's own Autocomplete pill is intentionally not this: it
// keeps its richer focus-glow markup untouched, per spec) ──
/** Height/min-width are the only bit that varies by sizeVariant, so these are functions (not
 * plain strings) to avoid ever having both a `h-10` and a `h-11` class present at once, which is
 * what emitting a base string plus a conditional override string in the same className would do. */
/** `isOpen` flattens the pill's bottom corners (and, via SUGGESTIONS_PANEL's matching
 * border-t-none, drops the seam between input and panel) so the two read as one continuous
 * shape while the dropdown is showing, matching how components/Autocomplete.tsx's own wrapper
 * flattens on the side the popover attaches to. Defaults to false so every existing prerendered
 * call site (initial SSR markup, before any JS toggles it) keeps rendering the plain pill. */
export function searchWrapperClass(sizeVariant: 'compact40' | 'compact44', isOpen = false): string {
  const height = sizeVariant === 'compact44' ? 'h-11' : 'h-10';
  // When open, the border grows to 2px (matching SUGGESTIONS_PANEL's border-l-2/border-r-2) and
  // drops its bottom edge, so the pill's own border continues seamlessly into the panel's border
  // below instead of leaving a visible 1px-vs-2px step at the seam. The background also switches
  // to the same translucent/blurred treatment as the panel (bg-jc-surface/95 + backdrop-blur-xl,
  // matching Autocomplete.tsx's own input, which flips to `bg-white/95` + blur when open) — an
  // opaque bar sitting directly on top of a translucent panel read as two different surfaces.
  const shape = isOpen
    ? 'border-2 border-b-0 rounded-t-full rounded-b-none bg-jc-surface/95 backdrop-blur-xl'
    : 'border rounded-full bg-jc-surface';
  return `min-w-0 flex-1 flex items-center max-w-[600px] ${height} ${shape} border-jc-border overflow-hidden transition-shadow duration-200 ease-out motion-reduce:transition-none focus-within:border-jc-accent focus-within:shadow-[0_0_0_4px_rgba(59,94,69,0.18),0_6px_28px_-6px_rgba(59,94,69,0.28)] dark:focus-within:shadow-[0_0_0_4px_rgba(152,207,168,0.16),0_6px_28px_-6px_rgba(152,207,168,0.30)]`;
}
export const SEARCH_ICON_WRAP = 'pl-4 pr-2 flex-shrink-0 text-jc-muted flex items-center';
/** `pr-10` (not the `px-1` this used to carry) leaves room for SEARCH_SHORTCUT_BADGE, which is
 * absolutely positioned over the input at `right-3` rather than laid out as a flex sibling:
 * without it the placeholder/typed text runs under the badge and gets clipped by the input's own
 * box edge (e.g. "Search jobs, skills or city" rendering as "...or cit"). Matches the `pr-10`
 * components/Autocomplete.tsx's own input already reserves for the identical badge. */
export const SEARCH_INPUT =
  'min-w-0 flex-1 h-full border-0 bg-transparent pl-1 pr-10 text-[13.5px] text-jc-ink placeholder:text-jc-muted focus:outline-none';
/** TEC navbar redesign item 5: the same "/" shortcut badge classes components/Autocomplete.tsx
 * renders (its own file stays untouched, per spec; these are copied, not imported, since
 * Autocomplete.tsx has no shared-styles dependency of its own to import from). Exported here so
 * the static header (ResourceNavbar.tsx) can render byte-identical markup instead of a
 * hand-drifted approximation. */
export const SEARCH_SHORTCUT_BADGE =
  'absolute right-3 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-full bg-[#F2EADF] dark:bg-[#2A1D0F] text-[#5A4A3A] dark:text-[#C4A882] text-xs font-mono font-semibold select-none pointer-events-none border border-[#DDD5C8] dark:border-[#4A3520] backdrop-blur-sm shadow-sm dark:shadow-none opacity-90';
export function locationSegmentClass(sizeVariant: 'compact40' | 'compact44'): string {
  const size = sizeVariant === 'compact44' ? 'h-11 min-w-[44px]' : 'h-10 min-w-[40px]';
  return `flex items-center justify-center gap-1.5 ${size} shrink-0 px-4 rounded-l-none rounded-r-full text-sm font-medium border-2 border-l-0 transition-all duration-200 cursor-pointer bg-jc-elevated text-jc-ink-secondary border-jc-border`;
}

// ── Language track (matches LanguageSelector.tsx sizeVariant="compact40") ──
export const LANG_TRACK =
  'hidden xl:flex items-center gap-1 h-10 px-1.5 rounded-full border border-[#DDD5C8]/70 dark:border-[#4A3520]/80 bg-white/90 dark:bg-[#1F1509]/90';
export const LANG_CIRCLE =
  'relative w-[30px] h-[30px] shrink-0 rounded-full overflow-hidden flex items-center justify-center text-xs font-extrabold uppercase cursor-pointer transition-colors';
export const LANG_FLAG_HALO = 'absolute inset-0 opacity-30';
export const LANG_TEXT_ACTIVE =
  'relative text-[#1E1A14] dark:text-[#F2EADF] [text-shadow:0_0_4px_white,0_0_4px_white,0_0_6px_white] dark:[text-shadow:0_0_4px_#1F1509,0_0_4px_#1F1509,0_0_6px_#1F1509]';
export const LANG_TEXT_INACTIVE = 'relative text-jc-ink-secondary hover:text-jc-ink';

// ── Account pill (matches nav-live-index/AccountPill.tsx's guest branch) ──
export const ACCOUNT_PILL =
  'inline-flex items-center gap-1.5 h-10 pl-1 pr-3.5 rounded-full border border-jc-border bg-jc-surface text-sm font-semibold text-jc-ink-secondary hover:bg-jc-elevated hover:text-jc-ink transition-colors whitespace-nowrap cursor-pointer';
export const ACCOUNT_AVATAR_GUEST =
  'flex-shrink-0 w-[30px] h-[30px] rounded-full border-[1.5px] border-dashed border-jc-muted text-jc-muted flex items-center justify-center';
export const ACCOUNT_AVATAR_MEMBER = 'flex-shrink-0 overflow-hidden rounded-full w-[30px] h-[30px] flex items-center justify-center';
/** Matches AccountPill.tsx's live badge (new-matches count for a signed-in member). Shared here
 * (TEC navbar redesign item 3) so the static header's script-rendered badge, filled from
 * GET /api/my-jobs-badge, can never drift from the hydrated pill's own badge markup. */
export const ACCOUNT_BADGE =
  'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-jc-accent text-jc-surface text-[11px] font-bold tabular-nums';

// ── Mobile bottom bar (TEC navbar redesign item 2; matches components/navigation/
// MobileBottomNav.tsx, extracted here so the static header (ResourceNavbar.tsx's twin, built by
// web-static/pages/landing/landing-nav-live-index-script.ts) can render the same chrome instead
// of a hand-drifted approximation). Button sizing itself stays a `liveIndex ? 'w-11 h-11' :
// 'p-3'` ternary in MobileBottomNav.tsx (unchanged, kept there rather than folded in here) since
// the flag-off classic size must keep rendering exactly as it did before this ticket; only the
// border/background/hover chrome common to both sizes lives here. ──
/** The exact same font stack MobileBottomNav.tsx's own `iosFontStack` constant declares
 * (kept as a separate export, not folded into MOBILE_BOTTOM_BAR_CONTAINER, so
 * MobileBottomNav.tsx's own `iosFontStack` variable can import and re-export this one without
 * changing its literal string, byte for byte). */
export const MOBILE_BOTTOM_BAR_FONT_STACK =
  "font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif]";
export const MOBILE_BOTTOM_BAR_CONTAINER =
  `fixed bottom-0 left-0 w-full z-[60] flex items-center gap-2 px-3 py-2 border-t border-[#DDD5C8] dark:border-[#4A3520] lg:hidden bg-white/95 dark:bg-[#1F1509]/95 shadow-[0_-4px_24px_rgba(0,0,0,0.06)] dark:shadow-none rounded-t-2xl transition-colors duration-300 ${MOBILE_BOTTOM_BAR_FONT_STACK} max-w-full overflow-hidden`;
export const MOBILE_BOTTOM_BAR_FILTERS_BUTTON_BASE =
  'relative flex items-center justify-center outline-none flex-none rounded-full font-bold border border-[#DDD5C8] dark:border-[#4A3520] hover:border-[#3B5E45]/50 dark:hover:border-[#98CFA8]/40 hover:bg-[#F2EADF] dark:hover:bg-[#342415] transition-all duration-200 cursor-pointer bg-white dark:bg-[#2A1D0F] shadow-sm dark:shadow-none';
export const MOBILE_BOTTOM_BAR_MENU_BUTTON_BASE =
  'flex items-center justify-center outline-none flex-none rounded-full font-bold border border-[#DDD5C8] dark:border-[#4A3520] hover:border-[#3B5E45]/40 dark:hover:border-[#98CFA8]/35 hover:bg-[#F2EADF] dark:hover:bg-[#342415] transition-all duration-200 cursor-pointer bg-white dark:bg-[#2A1D0F] shadow-sm dark:shadow-none';
/** The 44px size both buttons always use once the navbar live-index redesign is the only header
 * (the static twin, unlike MobileBottomNav.tsx, has no flag-off classic path: prerendered pages
 * always ship the new design, see ResourceNavbar.tsx's own doc comment). */
export const MOBILE_BOTTOM_BAR_BUTTON_SIZE = 'w-11 h-11';

// ── Menu button (matches nav-live-index/BarsMenuButton.tsx) ──
export const MENU_BUTTON_BASE =
  'flex items-center justify-center flex-shrink-0 rounded-full border border-jc-border bg-jc-surface text-jc-ink-secondary hover:bg-jc-elevated hover:text-jc-ink transition-colors cursor-pointer';
export const MENU_BUTTON_SIZE_DESKTOP = 'w-10 h-10';
export const MENU_BUTTON_SIZE_MOBILE = 'w-11 h-11 lg:w-10 lg:h-10';

// ── Row two index strip (matches nav-live-index/IndexStrip.tsx) ──
export const STRIP_CONTAINER =
  'hidden lg:flex items-center border-t border-jc-border bg-jc-surface overflow-hidden px-[22px] transition-[height,opacity] duration-[220ms] ease-out motion-reduce:transition-none';
export const STRIP_SCROLLER =
  'flex items-center gap-[22px] min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden flex-1';
export function stripFigureLinkClass(isActive: boolean): string {
  return `group relative inline-flex items-baseline gap-[5px] whitespace-nowrap py-1 text-[12.5px] no-underline ${
    isActive ? 'text-jc-accent' : 'text-jc-ink-secondary'
  }`;
}
export function stripFigureValueClass(isActive: boolean): string {
  return `inline-block empty:min-w-[5ch] font-display font-semibold text-[13.5px] tabular-nums tracking-[-0.01em] ${
    isActive ? 'text-jc-accent' : 'text-jc-ink'
  }`;
}
export function stripUnderlineClass(isActive: boolean): string {
  return `pointer-events-none absolute left-0 right-0 bottom-0 h-0.5 bg-jc-accent transition-transform duration-[180ms] ease-out origin-left ${
    isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
  }`;
}
export function stripWordLinkClass(isActive: boolean): string {
  return `group relative inline-flex items-baseline whitespace-nowrap py-1 text-[12.5px] font-medium no-underline ${
    isActive ? 'text-jc-accent' : 'text-jc-ink-secondary'
  }`;
}
export const STRIP_DIVIDER = 'w-px h-3.5 bg-jc-border flex-shrink-0';
export const STRIP_STAMP_WRAP = 'ml-auto inline-flex items-center gap-[7px] whitespace-nowrap pl-4 text-xs text-jc-muted flex-shrink-0';
export const STRIP_STAMP_DOT =
  'w-[7px] h-[7px] rounded-full bg-jc-accent motion-safe:animate-[ping_2.4s_ease-out_1] motion-reduce:animate-none';

// ── Search-suggestions dropdown (TEC navbar redesign Phase 4 item 2), shaped after the SRP's
// own Autocomplete popover (components/Autocomplete.tsx) and shared by the React fallback
// launcher (FallbackLauncher in SearchLauncher.tsx) and the static launcher's vanilla-JS island
// (web-static/pages/landing/landing-nav-live-index-script.ts), so the two dropdowns look alike.
//
// Visual-parity pass (2026-09-09): the original version above was only shaped like Autocomplete's
// popover in structure (header + rows), not in treatment, so the two dropdowns read as visibly
// different controls. This ports Autocomplete's frosted-glass rounded-3xl panel (backdrop blur,
// 2px border, inset shadow, left/right edge-fade gradients, "scroll for more" hint past
// SUGGESTIONS_SCROLL_HINT_THRESHOLD rows) so this simplified dropdown looks like the same
// component, not a plainer stand-in. Autocomplete.tsx itself stays untouched (hardcoded hex, no
// shared-styles dependency, per the original Phase 4 spec): these are token-based equivalents,
// not a byte-for-byte class copy. ──
export const SUGGESTIONS_PANEL =
  'absolute left-0 right-0 top-full z-[110] max-h-96 overflow-y-auto rounded-b-3xl rounded-t-none border-l-2 border-r-2 border-b-2 border-jc-border bg-jc-surface/95 backdrop-blur-xl shadow-[inset_0_4px_14px_0_rgba(0,0,0,0.12),inset_0_1px_4px_0_rgba(0,0,0,0.08)]';
/** Wraps the header + rows inside SUGGESTIONS_PANEL; the edge-fade gradients below are
 * absolutely positioned against this element (not the scrollable panel itself), matching
 * Autocomplete's own `<div className="relative py-2">` content wrapper. */
export const SUGGESTIONS_LIST_WRAP = 'relative py-2';
export const SUGGESTIONS_EDGE_FADE_LEFT =
  'absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-jc-surface/95 to-transparent pointer-events-none z-10';
export const SUGGESTIONS_EDGE_FADE_RIGHT =
  'absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-jc-surface/95 to-transparent pointer-events-none z-10';
export const SUGGESTIONS_HEADER =
  'px-5 pt-3 pb-2 text-xs font-semibold uppercase tracking-wider text-jc-muted flex items-center justify-between';
/** The "Scroll for more" hint shown next to the header once there are more rows than fit without
 * scrolling; matches Autocomplete's own `facetSuggestions.length > 5` threshold. */
export const SUGGESTIONS_SCROLL_HINT_THRESHOLD = 5;
export const SUGGESTIONS_HEADER_HINT = 'text-[10px] normal-case font-normal text-jc-muted';
export function suggestionsRowClass(isActive: boolean): string {
  // `!` (important) on the padding utilities: this row is a <button>, and on the static/prerendered
  // page it renders inside `.lp-root`, which carries a preflight-style reset (landing.css:
  // `.lp-root :where(button) { padding: 0 }`) that ties `px-5`/`py-3` on specificity and wins on
  // source order, silently zeroing the row's padding. components/Autocomplete.tsx's real popover
  // never hits this (it isn't nested under `.lp-root`), which is why only this dropdown went flat.
  return `flex w-full items-center justify-between gap-3 px-5! py-3! text-left text-xs font-medium transition-all duration-200 ${
    isActive ? 'bg-jc-elevated/80 text-jc-ink' : 'text-jc-ink hover:bg-jc-elevated/80 active:bg-jc-elevated-hover'
  }`;
}
export const SUGGESTIONS_ROW_ATTR_LABEL = 'text-[10.5px] uppercase tracking-wide text-jc-muted font-semibold';
export const SUGGESTIONS_ROW_COUNT = 'text-sm font-semibold text-jc-muted tabular-nums';
/** Leading tile on a suggestion row: the company logo for `companyId` rows, a facet icon otherwise. */
export const SUGGESTIONS_ROW_LEAD =
  'flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-jc-elevated text-jc-muted';
export const SUGGESTIONS_ROW_LEAD_ICON = 'w-4 h-4';
export const SUGGESTIONS_ROW_LEAD_LOGO = 'h-8 w-8 object-contain';
/** Left group of a row (lead tile + labels); `min-w-0` so long labels truncate instead of pushing the count out. */
export const SUGGESTIONS_ROW_MAIN = 'flex min-w-0 items-center gap-3';
export const SUGGESTIONS_ROW_TEXT = 'flex min-w-0 flex-col';
export const SUGGESTIONS_EMPTY = 'px-5 py-4 text-center text-sm text-jc-muted';
